import { SortDirection } from "@kamae-apps/shared/Components/Organisms/Table/types"
import { ApiResponse, RequestOptions, useApi } from "@kamae-apps/shared/Hooks/useApi"
import type { APIResponse } from "@kamae-apps/shared/Types/ApiResponse"
import type { SCOPE } from "@kamae-apps/shared/Types/Scope"
import type User from "@kamae-apps/shared/Types/User"
import { NewUser, USER_STATUS } from "@kamae-apps/shared/Types/User"
import { apiRequest, apiRequestRaw } from "@kamae-apps/shared/utils"
import { BatchResult } from "../Components/BatchErrorModal/type"

export type UpdateUserPayload = {
  email: string
  name: string
  first_name: string
  team: number
  status?: USER_STATUS
  scope?: SCOPE
}

class UserService {
  fetchUserData(): Promise<User> {
    try {
      return apiRequest<User>("/user?v2=true", { name: "v2" })
    } catch (error) {
      throw new Error(error)
    }
  }

  fetchUserV1Data(): Promise<User> {
    try {
      return apiRequest<User>("/user", { name: "v2" })
    } catch (error) {
      throw new Error(error)
    }
  }

  updateUserData(body: User): Promise<User> {
    try {
      return apiRequest("/user?v2=true", {
        name: "v2",
        method: "PUT",
        body,
      })
    } catch (error) {
      throw new Error(error)
    }
  }

  importUserBatch(csvContent: ArrayBuffer | string | null, languageId: number): Promise<BatchResult | null> {
    return apiRequestRaw<BatchResult>(`/users/batch?languageId=${languageId}`, "text/csv; charset=utf-8", {
      method: "POST",
      body: csvContent ?? undefined,
    })
  }

  getAuthMethod(email: string): Promise<APIResponse<string>> {
    return apiRequest<APIResponse<string>>(`/user/auth?mail=${email.toLowerCase()}`)
  }

  updateUserLanguage(code: string): Promise<unknown> {
    return apiRequest("/user/lang", {
      method: "POST",
      body: { code },
      headers: { "X-Targeted-Language": code },
    })
  }

  createUser(data: NewUser): Promise<User> {
    return apiRequest<User>("/user", { method: "POST", body: data })
  }

  updateUser(userId: number, data: UpdateUserPayload): Promise<User> {
    return apiRequest<User>(`/user/${userId}`, { method: "PUT", body: data })
  }

  deleteUser(userId: number): Promise<void> {
    return apiRequest<void>(`/user/${userId}`, { method: "DELETE" })
  }

  getAdminCount(): ApiResponse<number> {
    return useApi<number>("/companies/admin-count", {})
  }

  getSupervisionUsers(params: SupervisionUsersParams = {}, requestOptions?: RequestOptions): ApiResponse<User[]> {
    return useApi<User[]>(`/supervision/users${buildSupervisionUsersQuery(params)}`, requestOptions ?? {})
  }

  getSupervisionUsersCount(
    params: SupervisionUsersCountParams = {},
    requestOptions?: RequestOptions
  ): ApiResponse<number> {
    return useApi<number>(`/supervision/users/count${buildSupervisionUsersQuery(params)}`, requestOptions ?? {})
  }
}

type SupervisionUsersCountParams = {
  search?: string
  status?: USER_STATUS
  teams?: string[]
}

type SupervisionUsersParams = SupervisionUsersCountParams & {
  offset?: number
  limit?: number
  order?: SortDirection
  sorted?: string
}

const buildSupervisionUsersQuery = (params: SupervisionUsersParams): string => {
  const parts: string[] = []
  const pushSingle = (key: string, value: string | number | undefined) => {
    if (value !== undefined && value !== "") parts.push(`${key}=${encodeURIComponent(value)}`)
  }
  pushSingle("offset", params.offset)
  pushSingle("limit", params.limit)
  pushSingle("search", params.search)
  pushSingle("order", params.order)
  pushSingle("sorted", params.sorted)
  pushSingle("status", params.status)
  for (const name of params.teams ?? []) parts.push(`teams=${encodeURIComponent(name)}`)
  return parts.length > 0 ? `?${parts.join("&")}` : ""
}

export const userService = new UserService()
